import { API_URL } from './constants'
import { getItem, setItem, removeItem } from './storage'
import { STORAGE_KEYS } from './constants'
import { httpConnection } from './httpConnection'

/** Distinguishes server errors (with a status code) from network/timeout failures. */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable `code` from the error body, when the server sends one (e.g. 'AI_TAKEOVER'). */
    public readonly code?: string,
    /** The parsed error body, for codes that carry extra fields. */
    public readonly body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

/**
 * Retry fn up to `attempts` times with per-attempt delays.
 * 4xx errors are never retried — they are deterministic failures.
 * Network errors and 5xx are retried.
 */
export async function withRetry<T>(fn: () => Promise<T>, attempts: number, delays: number[]): Promise<T> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (err) {
      const isRetryable = !(err instanceof HttpError) || err.status >= 500
      if (i === attempts - 1 || !isRetryable) throw err
      await new Promise((resolve) => setTimeout(resolve, delays[i] ?? delays[delays.length - 1]))
    }
  }
  throw new Error('unreachable')
}

// 20 s: 8 s was too aggressive for mobile networks — cellular transitions,
// poor signal, and congested hotspots regularly need 10–15 s to complete a
// request. The previous 8 s generated 41 WARCARP-APP-B/-3 "Request timed out"
// Sentry events that were environmental noise rather than code bugs.
// Even if a request still times out at 20 s, reportableError now classifies
// it as a transient network error and downgraded to a breadcrumb so it no
// longer pollutes the error-level issue feed.
// Exported so a regression test can guard against re-tightening it back to an
// aggressive value (see api.test.ts).
export const FETCH_TIMEOUT_MS = 20_000

/**
 * Serialize a request body for a body-bearing method (POST / PATCH). When the
 * caller passes no body we send a literal empty JSON object `{}` — never an
 * absent/empty body.
 *
 * Why: React Native's Android (okhttp) networking turns a body-less POST into a
 * request with `Content-Length: 1` and NO `Content-Type`. Fastify rejects that
 * with **415 Unsupported Media Type** before the route handler runs — which is
 * the real, Android-only cause of the "Fortify spins but never advances" bug
 * (confirmed in Sentry: `FastifyError: Unsupported Media Type`,
 * `POST /turn/advance`, `User-Agent: okhttp`). iOS/web send a clean 0-byte body
 * and were unaffected.
 *
 * `{}` is valid, non-empty JSON, so it sidesteps BOTH the 415 above AND
 * Fastify's "Body cannot be empty when content-type is application/json" error
 * — on every platform.
 */
export function serializeBody(body?: unknown): string {
  return body != null ? JSON.stringify(body) : '{}'
}

/** Thrown when a request exceeds FETCH_TIMEOUT_MS. */
export const TIMEOUT_MESSAGE = 'Request timed out — check your connection'

/**
 * Race a promise against a timeout. If `ms` elapses first, run `onTimeout`
 * (used to abort the underlying request) and reject.
 *
 * Why not rely on `AbortController` + fetch `signal` alone? On New-Architecture
 * (bridgeless) Android, `controller.abort()` does NOT cancel an in-flight
 * `fetch`, so a stalled request hangs forever — the observed "Fortify spins but
 * never resolves" and "WS stops after the token expires mid-session" failures,
 * both of which route through a token-refresh `fetch`. Racing against a real
 * timer guarantees the promise settles regardless of whether `abort()` works.
 *
 * Pure (no fetch/RN dependency) so it can be unit-tested with fake timers.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout?: () => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => {
      onTimeout?.()
      reject(new Error(TIMEOUT_MESSAGE))
    }, ms)
    promise.then(
      (value) => {
        clearTimeout(id)
        resolve(value)
      },
      (err) => {
        clearTimeout(id)
        reject(err)
      },
    )
  })
}

/**
 * `fetch` that is guaranteed to settle within `timeoutMs`. Aborts the request
 * (best-effort) AND races a timer, so it terminates even where `abort()` is a
 * no-op. Every network call in this module goes through here — no bare `fetch`.
 */
export function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  return withTimeout(
    fetch(url, { ...init, signal: controller.signal }),
    timeoutMs,
    () => {
      try {
        controller.abort()
      } catch {
        // abort() can throw on some platforms; the timer rejection still fires.
      }
    },
  )
}

let forceLogoutCallback: (() => void) | null = null

export function registerForceLogout(callback: () => void) {
  forceLogoutCallback = callback
}

/**
 * Single-flight guard for refreshAccessToken. Concurrent callers — parallel
 * 401s, the WebSocket hook recovering from a 1008 close, and the cold-start
 * bootstrap — must share ONE in-flight refresh. The server ROTATES the refresh
 * token on every call, so two concurrent refreshes would each revoke the
 * other's token: the loser sends a now-revoked token, gets a 401, and forces a
 * spurious logout. That race was the "bounced to login after the token
 * expired" bug. Sharing the promise makes every caller resolve to the same new
 * access token from a single rotation.
 */
let refreshInFlight: Promise<string | null> | null = null

/**
 * Exchange the stored refresh token for a fresh access token (and rotate the
 * refresh token), persisting both. Returns the new access token, or null when
 * the session is genuinely dead (no refresh token, or the server rejected it
 * with 401/403 — in which case both tokens are cleared = logged out).
 *
 * THROWS on transient failures (network/timeout/5xx) WITHOUT clearing tokens,
 * so a flaky connection never logs the user out — the caller keeps the session
 * and the next request retries. Exported so the WebSocket hook can recover
 * from a 1008 auth-close the same way the HTTP layer recovers from a 401.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshAccessToken().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

async function doRefreshAccessToken(): Promise<string | null> {
  const refreshToken = await getItem(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken) return null

  const res = await fetchWithTimeout(`${API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })

  // Genuine auth failure — the refresh token is invalid/expired/revoked. This
  // is the ONLY case where we clear the session and signal a logout.
  if (res.status === 401 || res.status === 403) {
    await removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    await removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    return null
  }

  // Transient server failure (5xx, etc.) — DO NOT log out. Throw so the caller
  // treats it as temporary and preserves the session for the next attempt.
  if (!res.ok) {
    throw new HttpError(res.status, `Token refresh failed (${res.status})`)
  }

  const data = (await res.json()) as { accessToken: string; refreshToken: string }
  await setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken)
  await setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken)
  return data.accessToken
}

const RETRY_DELAYS_MS = [1_000, 2_000]

async function apiFetchOnce(path: string, options: RequestInit, accessToken: string | null): Promise<Response> {
  const headers = new Headers(options.headers)
  // Declare JSON whenever there's a body. POST/PATCH always carry one now
  // (body-less calls send `{}` via serializeBody — see why there), so this sets
  // the header for them and leaves bodyless GET/DELETE untouched.
  if (options.body !== undefined && options.body !== null) {
    headers.set('Content-Type', 'application/json')
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  return fetchWithTimeout(`${API_URL}${path}`, { ...options, headers })
}

export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const finish = httpConnection.begin(path)
  try {
    const response = await apiFetchWithAuth(path, options)
    // 4xx means the server was reached; it is not a connectivity failure.
    finish(response.status < 500)
    return response
  } catch (error) {
    finish(false)
    // The React Query layer reports the original error to monitoring.
    throw error
  }
}

async function apiFetchWithAuth(path: string, options: RequestInit): Promise<Response> {
  const accessToken = await getItem(STORAGE_KEYS.ACCESS_TOKEN)
  let res = await apiFetchOnce(path, options, accessToken)

  // Retry transient 5xx errors with backoff
  for (let i = 0; i < RETRY_DELAYS_MS.length && res.status >= 500; i++) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[i]))
    res = await apiFetchOnce(path, options, accessToken)
  }

  if (res.status === 401 && accessToken) {
    let newToken: string | null
    try {
      // Shared single-flight refresh (see refreshAccessToken). Concurrent 401s
      // all await the same rotation.
      newToken = await refreshAccessToken()
    } catch {
      // Transient refresh failure (network/timeout/5xx). Do NOT log out —
      // return the original 401 so this one request fails while the session
      // survives for the next attempt.
      return res
    }

    if (!newToken) {
      // Genuine auth failure: the refresh token was rejected and cleared.
      forceLogoutCallback?.()
      return res
    }

    // Retry the original request once with the fresh access token.
    return apiFetchOnce(path, options, newToken)
  }

  return res
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(path)
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string; error?: string; code?: string }
    throw new HttpError(res.status, err.message ?? err.error ?? `HTTP ${res.status}`, err.code, err)
  }
  return res.json() as Promise<T>
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    body: serializeBody(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string; error?: string; code?: string }
    throw new HttpError(res.status, err.message ?? err.error ?? `HTTP ${res.status}`, err.code, err)
  }
  return res.json() as Promise<T>
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'PATCH',
    body: serializeBody(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string; error?: string; code?: string }
    throw new HttpError(res.status, err.message ?? err.error ?? `HTTP ${res.status}`, err.code, err)
  }
  return res.json() as Promise<T>
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  const res = await apiFetch(path, { method: 'DELETE' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string; error?: string; code?: string }
    throw new HttpError(res.status, err.message ?? err.error ?? `HTTP ${res.status}`, err.code, err)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ─── Auth helpers ────────────────────────────────────────────────────────────

import type {
  LoginRequest,
  TokenResponse,
  RegisterRequest,
  RegisterResponse,
  ConfirmEmailRequest,
  ConfirmEmailResponse,
  ResendConfirmationRequest,
  ResendConfirmationResponse,
} from '@warcarp/api-types'

export async function authLogin(data: LoginRequest): Promise<TokenResponse> {
  return apiPost<TokenResponse>('/api/v1/auth/login', data)
}

export async function authRegister(data: RegisterRequest): Promise<RegisterResponse> {
  return apiPost<RegisterResponse>('/api/v1/auth/register', data)
}

export async function confirmEmail(token: string): Promise<ConfirmEmailResponse> {
  return apiPost<ConfirmEmailResponse>('/api/v1/auth/confirm-email', { token } satisfies ConfirmEmailRequest)
}

export async function resendConfirmation(email: string): Promise<ResendConfirmationResponse> {
  return apiPost<ResendConfirmationResponse>('/api/v1/auth/resend-confirmation', { email } satisfies ResendConfirmationRequest)
}

// Backwards compat: keep apiJson for existing callers
export async function apiJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()
  if (method === 'GET') return apiGet<T>(path)
  if (method === 'DELETE') return apiDelete<T>(path)
  const body = options.body != null ? JSON.parse(options.body as string) : undefined
  if (method === 'PATCH') return apiPatch<T>(path, body)
  return apiPost<T>(path, body)
}
